import {
  AppCommandsResponse,
  CommandsResponse,
  CreateCommandParam,
  DeleteCommandParam,
  ExecuteCommandActionParam,
  ExecuteCommandParam,
  ExecuteCommandSubmitParam,
  ExternalOptionsResponse,
  RegisterCommandParam,
  SlashCommand,
  SlashCommandApp,
  ValidateCommandNameParam,
} from '@@types/Api/Command';
import Attachment, { AttachmentAction } from '@@types/Attachment';
import { CHANNEL_ID, COMMAND_ID, SLASH_COMMAND_APP_ID } from '@@types/common';
import { CommandLog } from '@@types/Log';

import { URLS } from '~/constants';
import Api2 from '~/stores/providers/Api2';

class CommandApi {
  constructor(private api: Api2) {}

  fetchRegistrableApps = (): Promise<CommandsResponse> => {
    return this.api.contentsRequester.get(`${URLS.API}/commands`);
  };

  fetchChannelApps = (channelId: CHANNEL_ID): Promise<CommandsResponse> => {
    return this.api.contentsRequester.cancelGet(`${URLS.API}/channels/${channelId}/commands`, channelId);
  };

  fetchCommandById = (commandId: COMMAND_ID): Promise<SlashCommand> => {
    return this.api.contentsRequester.get(`${URLS.API}/commands/${commandId}`);
  };

  fetchCommands = (appId: SLASH_COMMAND_APP_ID): Promise<AppCommandsResponse> => {
    return this.api.contentsRequester.get(`${URLS.API}/apps/${appId}/commands`);
  };

  validateDuplicatedIntegrationCommandName = ({ appId, name }: ValidateCommandNameParam): Promise<boolean> => {
    return this.api.contentsRequester.get(`${URLS.API}/apps/${appId}/commands`, {
      params: { name },
    });
  };

  createCommand = ({ appId, ...params }: CreateCommandParam): Promise<SlashCommand> => {
    return this.api.contentsRequester.post(`${URLS.API}/apps/${appId}/commands`, params);
  };

  updateCommand = (command: Partial<SlashCommand>): Promise<SlashCommand> => {
    return this.api.contentsRequester.put(
      `${URLS.API}/apps/${command.integrationAppId}/commands/${command.id}`,
      command
    );
  };

  deleteCommand = ({ appId, commandId }: DeleteCommandParam): Promise<number> => {
    return this.api.contentsRequester.delete(`${URLS.API}/apps/${appId}/commands/${commandId}`);
  };

  registerCommand = ({ channelId, command }: RegisterCommandParam): Promise<string> => {
    return this.api.contentsRequester.post(`${URLS.API}/channels/${channelId}/commands`, {
      commandId: command.id,
      appId: command.integrationAppId,
    });
  };

  unregisterCommand = ({ channelId, command }: RegisterCommandParam): Promise<string> => {
    return this.api.contentsRequester.delete(`${URLS.API}/channels/${channelId}/commands/${command.id}`);
  };

  executeAction = ({ commandId, ...params }: ExecuteCommandActionParam): Promise<string> => {
    return this.api.contentsRequester.post(`${URLS.API}/commands/${commandId}/action`, params);
  };

  executeCommand = ({ commandId, ...params }: ExecuteCommandParam): Promise<string> => {
    return this.api.contentsRequester.post(`${URLS.API}/commands/${commandId}`, params);
  };

  submitCommandModal = ({
    channelId,
    token,
    callbackId,
    triggerId,
    form: submission,
  }: ExecuteCommandSubmitParam): Promise<null> => {
    return this.api.contentsRequester.post(`${URLS.API}/channels/${channelId}/dialogs/submit`, {
      submission,
      callbackId,
      token,
      triggerId,
    });
  };

  fetchExternalOptions = (
    { id, token }: SlashCommandApp,
    { callbackId, commandId }: Attachment,
    action: AttachmentAction,
    log: CommandLog
  ): Promise<ExternalOptionsResponse> => {
    return this.api.contentsRequester.post(`${URLS.API}/commands/${commandId}/optional`, {
      callbackId,
      actions: {
        appId: id,
        channelId: log.channelId,
        channelLogId: log.id,
        actionName: action.name,
        actionText: action.text,
        actionValue: action.value,
        cmdToken: token,
        originalMessage: JSON.stringify(log),
      },
    });
  };
}

export default CommandApi;
